import React from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Badge,
  Divider,
  Button,
  Link,
  HStack,
  Image,
} from "@chakra-ui/react";
import {
  FiShield,
  FiTruck,
  FiHeadphones,
  FiTool,
  FiCheckCircle,
  FiPackage,
  FiClock,
  FiAward,
  FiCpu,
  FiHardDrive,
  FiMonitor,
  FiSettings,
} from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";

const About: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg={useColorModeValue("gray.50", "gray.900")}
        py={20}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="1400px">
          <VStack spacing={8} align="center" textAlign="center">
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              Your Trusted Tech Partner
            </Badge>
            <Heading
              size="2xl"
              bgGradient="linear(to-r, green.500, green.600)"
              bgClip="text"
              fontWeight="bold"
            >
              About TechParts Hub
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="800px">
              Your one-stop destination for premium computer components and expert
              technical support. We're committed to providing the best hardware
              solutions for your computing needs.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Our Story Section */}
          <Box>
            <VStack spacing={8} align="stretch">
              <Heading size="xl">Our Story</Heading>
              <Text fontSize="lg" color="gray.600">
                Founded in 2020, TechParts Hub emerged from a passion for
                technology and a vision to provide high-quality computer components
                to the Pakistani market. What started as a small shop in Karachi
                has grown into a nationwide network of tech enthusiasts dedicated
                to serving our customers with the best hardware solutions.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="start">
                    <Icon as={FiAward} w={8} h={8} color="green.500" />
                    <Heading size="md">Quality First</Heading>
                    <Text color="gray.600">
                      We partner with leading manufacturers to ensure every
                      component meets the highest standards of quality and
                      reliability.
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="start">
                    <Icon as={FiTool} w={8} h={8} color="green.500" />
                    <Heading size="md">Expert Support</Heading>
                    <Text color="gray.600">
                      Our team of certified technicians provides expert guidance
                      and support for all your hardware needs.
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="start">
                    <Icon as={FiClock} w={8} h={8} color="green.500" />
                    <Heading size="md">Fast Delivery</Heading>
                    <Text color="gray.600">
                      We understand the importance of quick delivery and ensure
                      your components reach you safely and on time.
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>

          <Divider />

          {/* Why Choose Us Section */}
          <Box>
            <VStack spacing={8} align="stretch">
              <Heading size="xl">Why Choose Us</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiShield} w={8} h={8} color="green.500" />
                    <Heading size="md">Genuine Products</Heading>
                    <Text color="gray.600">
                      100% authentic components with manufacturer warranty
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiTruck} w={8} h={8} color="green.500" />
                    <Heading size="md">Free Shipping</Heading>
                    <Text color="gray.600">
                      Free delivery on orders above Rs. 50,000
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiHeadphones} w={8} h={8} color="green.500" />
                    <Heading size="md">24/7 Support</Heading>
                    <Text color="gray.600">
                      Round-the-clock technical assistance
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiPackage} w={8} h={8} color="green.500" />
                    <Heading size="md">Easy Returns</Heading>
                    <Text color="gray.600">
                      7-day hassle-free return policy
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>

          <Divider />

          {/* Our Commitment Section */}
          <Box>
            <VStack spacing={8} align="stretch">
              <Heading size="xl">Our Commitment</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <VStack spacing={4} align="start">
                    <Heading size="md">Customer Satisfaction</Heading>
                    <Text color="gray.600">
                      We prioritize customer satisfaction above everything else. Our
                      dedicated team works tirelessly to ensure you get the best
                      products and service.
                    </Text>
                    <Button
                      as={RouterLink}
                      to="/contact"
                      colorScheme="green"
                      leftIcon={<FiCheckCircle />}
                    >
                      Contact Us
                    </Button>
                  </VStack>
                </Box>
                <Box>
                  <VStack spacing={4} align="start">
                    <Heading size="md">Quality Assurance</Heading>
                    <Text color="gray.600">
                      Every component in our inventory undergoes rigorous quality
                      checks. We partner with trusted manufacturers and suppliers
                      to maintain the highest standards.
                    </Text>
                    <Button
                      as={RouterLink}
                      to="/products"
                      colorScheme="green"
                      leftIcon={<FiPackage />}
                    >
                      View Products
                    </Button>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>

          {/* CTA Section */}
          <Box
            bg={useColorModeValue("gray.50", "gray.900")}
            p={8}
            borderRadius="lg"
            textAlign="center"
          >
            <VStack spacing={4}>
              <Heading size="xl">Ready to Build Your Dream PC?</Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                Browse our extensive collection of premium computer components and
                start building your perfect system today.
              </Text>
              <Button
                as={RouterLink}
                to="/products"
                colorScheme="green"
                size="lg"
                leftIcon={<FiPackage />}
              >
                Shop Now
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default About;
